import { Group, Loader, Stack, Text, Textarea, Tooltip, UnstyledButton, VisuallyHidden } from "@mantine/core";
import { useParams } from "@tanstack/react-router";
import { Archive, AudioLines, AudioWaveform, CircleAlert, ScanSearch, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { Voice } from "../../../api/generated/model";
import {
  AudioTtsQuote,
  AudioTtsRequest,
  VideoLipsyncQuote,
  VideoLipsyncRequest,
} from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { ProductListPopover, type ListedProduct } from "../../../platform/media/ProductListPopover";
import slots from "../../../platform/media/slots.module.css";
import { ScriptActions, ScriptBusy } from "../../../platform/scripts/ScriptActions";
import { ScriptOverlay } from "../../../platform/scripts/ScriptOverlay";
import { useScriptJobs } from "../../../platform/scripts/use-script-jobs";
import { VoicePanel } from "../../../platform/voices/VoicePanel";
import { AudioPlayer } from "../../../ui/AudioPlayer";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ModelPicker } from "../../../ui/ModelPicker";
import { Pill } from "../../../ui/Pill";
import { Price } from "../../../ui/Price";
import { SourceTool } from "../../../ui/SourceTool";
import {
  Errors,
  ROUTE,
  StartGroup,
  UNPRICED,
  VideoSlot,
  defaultModel,
  rowsOf,
  type ToolFormProps,
} from "./form-parts";
import classes from "./lipsync.module.css";
import { StatusButton, type StatusStep } from "./StatusButton";
import { Stability } from "./talking-head/CastVoice";
import { useTakes } from "./talking-head/use-takes";
import { useToolVideo } from "./use-tool-video";

/** Live's words (`lip-sync-form/constants.ts`, `generate-audio-button/constants.ts`). */
const PLACEHOLDER =
  "Write your script... Use [emotion] to add emotions like [laughs], [sights], [excited]";
/** Live's brief placeholder on Generate Script (`script-generation-overlay`). */
const BRIEF = "Describe what your speaker should say...";
/** Live's smiley takes any script: lip-sync's actions pass no shortest length (live's default, 1). */
const MIN_ENHANCE = 1;
const SYNC_TIP = "Defines how to handle duration mismatches between video and audio inputs";
const DETECT_TIP = "Whether to automatically detect and apply generation to the active speaker";
const NEEDS_VOICE = "Select a voice to generate audio";
const NEEDS_SCRIPT = "Add a script to generate audio";
const FAILED = "Failed to generate audio";
const SCRIPT_CHANGED = "The script was changed and you should re-generate the audio";
const VOICE_CHANGED = "The voice was changed and you should re-generate the audio";
/** Live holds "Audio generated" this long before "Re-generate Audio". */
const MADE_HOLD_MS = 3000;

type Word = "Generate Audio" | "Generating Audio" | "Audio generated" | "Re-generate Audio";
const STEPS: Record<Word, StatusStep> = {
  "Generate Audio": "generate",
  "Generating Audio": "generating",
  "Audio generated": "generated",
  "Re-generate Audio": "regenerate",
};

/**
 * Live's Lip-Sync form (#216, `lip-sync-form`, captures 12 to 12d, actions-raw 12): the "Video"
 * slot and "Voice Settings" (live's voice panel, with Stability under Advanced); the script and
 * "Generate Audio", step 1, a speech of the voice saying the script (`audio.tts`, the catalog's
 * speech row, live's `eleven_v3`); then the model, live's sync mode ("Cut Off" first) and
 * "Auto-detected", the price and the start, step 2, the video re-driven to that speech. A new
 * script or voice clears the speech, with live's words; a new stability does not, as live's.
 * Live's script tools (G8-script-tools): the wand opens "Generate Script" with a product for that
 * brief only, the smiley enhances; both wait while a script, an enhancement or the speech runs.
 */
export function LipSyncForm({ catalog, above, overlay, onStarted, inline }: ToolFormProps) {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const models = rowsOf(catalog, "video.lipsync");
  const [modelId, setModelId] = useState(() => defaultModel(models).id);
  const model = models.find((each) => each.id === modelId) ?? defaultModel(models);
  const caps = model.modes["video.lipsync"]?.lipsync ?? null;
  const speech = catalog.audio_models.find((each) => each.job_types.includes("audio.tts"));
  const [collapsed, setCollapsed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [voice, setVoice] = useState<Voice | null>(null);
  const [stability, setStability] = useState<number | null>(null);
  const [script, setScript] = useState("");
  const [take, setTake] = useState<string | null>(null);
  // A speech has been asked for since the form opened: "Re-generate Audio" from then on.
  const [generated, setGenerated] = useState(false);
  const [stale, setStale] = useState<string | null>(null);
  const [keptMode, setMode] = useState<string | null>(null);
  const [scriptOpen, setScriptOpen] = useState(false);
  // Live's product only seeds the brief, so it lives and dies with the overlay.
  const [product, setProduct] = useState<ListedProduct | null>(null);
  const [productsOpen, setProductsOpen] = useState(false);
  const [detect, setDetect] = useState(true);
  const tool = useToolVideo(teamId, brandId);
  const [made] = useTakes(teamId, [take]);
  const state = made ?? { state: "none" as const };
  // Live's model defaults: a mode the model lacks falls to its default.
  const modes = caps?.sync_modes ?? [];
  const mode =
    keptMode !== null && modes.some((each) => each.value === keptMode)
      ? keptMode
      : (caps?.default_sync_mode ?? null);
  const detects = caps?.auto_detect_speaker === true;
  const audio = state.state === "done" && take !== null ? take : null;

  // Step 1: the speech, priced and started as every job. It is the lip-sync's input (#514,
  // live's `GENERATION_INPUT`): off the lists, not saved, gone with its last lip-sync.
  const words = {
    type: "audio.tts" as const,
    input_of: "video.lipsync" as const,
    ...(speech === undefined ? {} : { model: speech.id }),
    prompt: script,
    ...(voice === null ? {} : { voice_id: voice.id }),
    ...(stability === null ? {} : { stability }),
  };
  const speechQuote = AudioTtsQuote.safeParse(words);
  const speechRequest = AudioTtsRequest.safeParse(words);
  const blockers = [
    ...(voice === null ? [NEEDS_VOICE] : []),
    ...(script.trim() === "" ? [NEEDS_SCRIPT] : []),
  ];
  const speaking = usePricedStart({
    teamId,
    brandId,
    quote: blockers.length === 0 && speechQuote.success ? speechQuote.data : undefined,
    request: blockers.length === 0 && speechRequest.success ? speechRequest.data : undefined,
    onStarted: (job) => {
      setTake(job.id);
      setGenerated(true);
      setStale(null);
    },
  });
  const running = state.state === "running" || speaking.start.isPending;
  // Live's "Audio generated" for 3 s once the speech is made.
  const [justMade, setJustMade] = useState(false);
  const wasRunning = useRef(false);
  const hold = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (wasRunning.current && !running && state.state === "done") {
      setJustMade(true);
      clearTimeout(hold.current);
      hold.current = setTimeout(() => setJustMade(false), MADE_HOLD_MS);
    }
    wasRunning.current = running;
  }, [running, state.state]);
  useEffect(() => () => clearTimeout(hold.current), []);
  let word: Word = generated ? "Re-generate Audio" : "Generate Audio";
  if (running) word = "Generating Audio";
  else if (justMade) word = "Audio generated";

  // Live's rules: a new script or voice clears the speech made, with its words.
  const clear = (why: string) => {
    if (take !== null && !running) {
      setTake(null);
      setStale(why);
    }
  };
  const write = (next: string) => {
    setScript(next);
    clear(SCRIPT_CHANGED);
  };
  // Live's script tools: a script or an enhancement lands as an edit does.
  const scripts = useScriptJobs({ teamId, brandId, tool: "lip_sync", onScript: write });
  const toolsFree = !running && !scripts.processing;
  const closeScript = () => {
    setScriptOpen(false);
    setProduct(null);
    setProductsOpen(false);
  };
  const productTool = (
    <ProductListPopover
      teamId={teamId}
      brandId={brandId}
      chosenId={product?.productId ?? null}
      opened={productsOpen}
      onClose={() => setProductsOpen(false)}
      onPick={setProduct}
      position="top-start"
      anchor={(wrap) => (
        <SourceTool
          label="Products"
          icon={<Archive size={20} strokeWidth={1.5} />}
          view={product}
          removeLabel="Remove the product"
          onRemove={() => setProduct(null)}
          onClick={() => setProductsOpen((open) => !open)}
          open={productsOpen}
          disabled={scripts.generating}
          target={wrap}
        />
      )}
    />
  );

  // Step 2: the lip-sync, once the video and the speech are there.
  const fields = {
    type: "video.lipsync" as const,
    model: model.id,
    ...(tool.ref === null ? {} : { source_video: tool.ref }),
    ...(audio === null ? {} : { source_audio: { kind: "job_output" as const, job_id: audio, output_index: 0 } }),
    ...(mode === null ? {} : { sync_mode: mode }),
    ...(detects ? { auto_detect: detect } : {}),
  };
  const quote = VideoLipsyncQuote.safeParse(fields);
  const request = VideoLipsyncRequest.safeParse(fields);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success ? quote.data : undefined,
    request: request.success ? request.data : undefined,
    onStarted,
  });

  return (
    <GlassPanel
      inline={inline}
      collapsed={collapsed}
      onCollapsedChange={(next) => {
        setCollapsed(next);
        setEditing(false);
      }}
      header={
        <div className={slots.slots}>
          <VideoSlot tool={tool} />
          <UnstyledButton
            className={slots.slot}
            aria-label={voice?.name ?? "Voice Settings"}
            disabled={running}
            onClick={() => setEditing(true)}
          >
            <AudioLines size={20} strokeWidth={1.5} aria-hidden />
            <span className={slots.slotLabel}>{voice?.name ?? "Voice Settings"}</span>
          </UnstyledButton>
          {state.state === "running" ? (
            <span className={slots.slotFilled}>
              <span className={slots.slotChosen} aria-label="Making the audio">
                <Loader size={20} />
              </span>
            </span>
          ) : null}
          {state.state === "done" ? (
            <span className={`${slots.slotFilled} ${classes.audio}`}>
              <AudioPlayer url={state.url} name="Audio" durationSeconds={(state.durationMs ?? 0) / 1000} />
              <UnstyledButton
                className={slots.thumbRemove}
                aria-label="Remove the audio"
                onClick={() => setTake(null)}
              >
                <X size={14} />
              </UnstyledButton>
            </span>
          ) : null}
        </div>
      }
      summary={<span>Lip-Sync</span>}
      above={above}
      overlay={overlay}
      cover={
        scriptOpen ? (
          <ScriptOverlay
            placeholder={BRIEF}
            footerLeft={productTool}
            pending={scripts.generating}
            error={scripts.generateError}
            onCancel={closeScript}
            onGenerate={(brief) => scripts.generate(brief, product?.productId ?? null, closeScript)}
          />
        ) : undefined
      }
      aside={
        editing ? (
          <VoicePanel
            teamId={teamId}
            brandId={brandId}
            chosen={voice}
            designModel={catalog.audio_models.find((each) => each.job_types.includes("voice.design"))}
            onChoose={(next) => {
              if (next.id !== voice?.id) clear(VOICE_CHANGED);
              setVoice(next);
            }}
            onClose={() => setEditing(false)}
            head={
              <div className={classes.head}>
                <AudioLines size={16} aria-hidden />
                <span>{voice?.name ?? "Select Voice"}</span>
              </div>
            }
            foot={<Stability value={stability} onChange={setStability} />}
          />
        ) : undefined
      }
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <Group gap={8} wrap="nowrap">
            <ModelPicker models={models} value={model.id} onChange={setModelId} />
            {mode === null || modes.length === 0 ? null : (
              <Choice
                setting="Sync mode"
                icon={<AudioWaveform size={20} strokeWidth={1.5} />}
                chevron={false}
                tip={SYNC_TIP}
                value={mode}
                options={modes.map((each) => ({ value: each.value, label: each.label || each.value }))}
                onChange={setMode}
              />
            )}
            {detects ? (
              <Tooltip label={DETECT_TIP} multiline maw={300} withArrow>
                <Pill
                  label="Auto-detected"
                  icon={<ScanSearch size={20} strokeWidth={1.5} />}
                  active={detect}
                  aria-pressed={detect}
                  onClick={() => setDetect(!detect)}
                />
              </Tooltip>
            ) : null}
          </Group>
          <StartGroup job={job} notes={model.modes["video.lipsync"]?.notes ?? []} />
        </Group>
      }
    >
      <Stack gap={16}>
        <Stack gap={4}>
          <div className={classes.scriptHead}>
            <Text className={classes.scriptLabel}>Script</Text>
            <ScriptActions
              canGenerate={toolsFree}
              canEnhance={toolsFree && script.trim().length >= MIN_ENHANCE}
              enhancing={scripts.enhancing}
              failed={scripts.scriptFailed}
              onGenerate={() => setScriptOpen(true)}
              onEnhance={() => scripts.enhance(script)}
            />
          </div>
          <ScriptBusy busy={scripts.processing}>
            <Textarea
              variant="unstyled"
              aria-label="Script"
              placeholder={PLACEHOLDER}
              autosize
              minRows={3}
              maxRows={8}
              disabled={running || scripts.processing}
              value={script}
              onChange={(event) => write(event.currentTarget.value)}
            />
          </ScriptBusy>
          {scripts.enhanceError === null ? null : <ErrorAlert error={scripts.enhanceError} />}
        </Stack>
        <div className={classes.audios}>
          <StatusButton
            step={STEPS[word]}
            label={word}
            disabled={blockers.length > 0 || running || !speaking.canStart}
            loading={speaking.start.isPending}
            blockers={blockers}
            onClick={() => {
              setEditing(false);
              speaking.startJob();
            }}
          />
          <Price priced={speaking.priced} pending={speaking.pricing} unpriced={UNPRICED} />
          {state.state === "failed" ? <Mark label={FAILED} tone="failed" /> : null}
          {stale !== null && state.state !== "failed" && !running ? <Mark label={stale} tone="stale" /> : null}
        </div>
        {speaking.quoteError === null ? null : <ErrorAlert error={speaking.quoteError} />}
        {speaking.start.isError ? <ErrorAlert error={speaking.start.error} /> : null}
        <Errors job={job} uploads={[tool.upload]} />
      </Stack>
      {tool.dialog}
    </GlassPanel>
  );
}

/** Live's marks beside Generate Audio: a failure in red, a changed script or voice in amber. */
function Mark({ label, tone }: { label: string; tone: "failed" | "stale" }) {
  return (
    <Tooltip label={label} withArrow>
      <span className={classes[tone]}>
        <CircleAlert size={20} aria-hidden />
        <VisuallyHidden>{label}</VisuallyHidden>
      </span>
    </Tooltip>
  );
}
